import React, { useState, useEffect, useRef } from 'react';
import { usePageTitle } from '../hooks/usePageTitle.js';
import { useNavigate } from 'react-router-dom';
import { TabSong } from '../types/index.js';
import {
  getLocalTabs,
  saveLocalTab,
  deleteLocalTab,
  getLocalFavorites,
  toggleFavoriteTab,
  getLocalPlaylists,
  saveLocalPlaylist,
  renameLocalPlaylist,
  deleteLocalPlaylist,
  addTabToPlaylist,
  retryTabUpload,
  stageDeletion
} from '../db/operations.js';
import { LocalPlaylist } from '../db/index.js';
import { toPersianDigits } from '../utils/timeUtils.js';
import { requestFavoriteSync } from '../sync/favoriteSync.js';
import { requestPlaylistSync } from '../sync/playlistSync.js';
import { newId } from '../utils/id.js';
import { TabImportDialog } from '../components/tab/TabImportDialog.js';
import { TabDetailsDialog } from '../components/tabEdits/TabDetailsDialog.js';
import { TAB_EDIT_COPY } from '../utils/tabEdits.js';
import { isLocalOnlyTab } from '../utils/difficulty.js';
import { ModerationBadge } from '../components/tab/ModerationBadge.js';
import { RejectReasonNote } from '../components/tab/RejectReasonNote.js';
import { TabBackupBadge } from '../components/tab/TabBackupBadge.js';
import { refreshOwnTabStatuses } from '../sync/tabStatusSync.js';
import { LIBRARY_CHANGED_EVENT } from '../sync/events.js';
import { requestTabUploadSync } from '../sync/tabUploadSync.js';
import { useAuthStore } from '../stores/authStore.js';
import { fold } from '../utils/textNormalize.js';
import { TabCard } from '../components/tab/TabCard.js';
import { DifficultySummary } from '../components/tab/DifficultyMeter.js';
import { PageHeader } from '../components/ui/PageHeader.js';
import { Button } from '../components/ui/Button.js';
import { IconButton } from '../components/ui/IconButton.js';
import { Segmented } from '../components/ui/Segmented.js';
import { SearchInput, Field, Input } from '../components/ui/Field.js';
import { Chip } from '../components/ui/Chip.js';
import { EmptyState } from '../components/ui/EmptyState.js';
import { Dialog } from '../components/ui/Dialog.js';
import { AnchoredMenu } from '../components/ui/AnchoredMenu.js';
import { useToast } from '../components/ui/index.js';
import { useTopBarStore } from '../stores/topBarStore.js';
import {
  Music,
  Heart,
  Plus,
  ListPlus,
  Upload,
  MoreHorizontal,
  LayoutGrid,
  List,
  Search,
  FolderPlus,
  Trash2,
  ChevronLeft,
  ChevronRight,
  ArrowUp,
  ArrowDown,
  Pencil,
  RefreshCw
} from 'lucide-react';

import { tuningBadge } from '../data/tunings.js';

const TUNING_OPTIONS = [
  { value: 'all', label: 'همه کوک‌ها' },
  { value: 'E Std', label: 'E Std' },
  { value: 'Drop D', label: 'Drop D' },
  { value: 'Drop C', label: 'Drop C' },
  { value: 'Drop A#', label: 'Drop A#' },
  { value: 'B Std 7', label: 'B Std 7' }
];

type SortKey = 'title' | 'artist' | 'date';
type SortDir = 'asc' | 'desc';

const SORT_OPTIONS: { value: SortKey; label: string }[] = [
  { value: 'date', label: 'تاریخ افزودن' },
  { value: 'title', label: 'عنوان' },
  { value: 'artist', label: 'هنرمند' }
];

const SORT_STORAGE_KEY = 'library-sort';

function loadSortPreference(): { key: SortKey; dir: SortDir } {
  try {
    const raw = localStorage.getItem(SORT_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as { key?: string; dir?: string };
      const key = SORT_OPTIONS.find((o) => o.value === parsed.key)?.value;
      if (key && (parsed.dir === 'asc' || parsed.dir === 'desc')) return { key, dir: parsed.dir };
    }
  } catch {
    // storage unavailable or corrupt: fall back to the default
  }
  return { key: 'date', dir: 'desc' };
}

function compareTabs(a: TabSong, b: TabSong, key: SortKey): number {
  if (key === 'date') {
    const at = a.createdAt ? Date.parse(a.createdAt) : 0;
    const bt = b.createdAt ? Date.parse(b.createdAt) : 0;
    return (Number.isNaN(at) ? 0 : at) - (Number.isNaN(bt) ? 0 : bt);
  }
  return a[key].localeCompare(b[key], ['fa', 'en'], { sensitivity: 'base', numeric: true });
}

const formatTuningBadge = tuningBadge;

function matchesTuning(tabTuning: string, selectedTuning: string): boolean {
  if (!selectedTuning || selectedTuning === 'all' || selectedTuning === 'ALL') return true;
  // E Std / E Standard / Standard E are one tuning (FRE-139): compare the normalised badges
  return tuningBadge(tabTuning).toLowerCase() === selectedTuning.toLowerCase();
}

export const LibraryPage: React.FC = () => {
  usePageTitle('کتابخانه تبلچر');
  const navigate = useNavigate();
  const toast = useToast();
  const isGuest = useAuthStore((st) => st.isGuest);

  const [tabs, setTabs] = useState<TabSong[]>([]);
  const [favoriteTabIds, setFavoriteTabIds] = useState<Set<string>>(new Set());
  const [playlists, setPlaylists] = useState<LocalPlaylist[]>([]);
  const [selectedPlaylistId, setSelectedPlaylistId] = useState<string | null>(null);

  // Filter & view mode state
  const [activeSegment, setActiveSegment] = useState<'all' | 'favorites' | 'playlists'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTuning, setSelectedTuning] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortKey, setSortKey] = useState<SortKey>(() => loadSortPreference().key);
  const [sortDir, setSortDir] = useState<SortDir>(() => loadSortPreference().dir);
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return window.innerWidth < 1024;
  });

  // Modals state
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [editDetailsTab, setEditDetailsTab] = useState<TabSong | null>(null);
  const [isNewPlaylistModalOpen, setIsNewPlaylistModalOpen] = useState(false);
  const [newPlaylistTitle, setNewPlaylistTitle] = useState('');
  const [renamePlaylistId, setRenamePlaylistId] = useState<string | null>(null);
  const [renamePlaylistTitle, setRenamePlaylistTitle] = useState('');
  const [playlistTargetTabId, setPlaylistTargetTabId] = useState<string | null>(null);

  const handleOpenEditDetails = (targetTab: TabSong) => {
    if (isGuest && !isLocalOnlyTab(targetTab)) {
      const next = encodeURIComponent(window.location.pathname + window.location.search);
      toast.info(TAB_EDIT_COPY.c20, {
        label: TAB_EDIT_COPY.c20Action,
        onClick: () => navigate(`/auth?next=${next}`)
      });
      return;
    }
    setEditDetailsTab(targetTab);
  };

  // Mobile more menu for stacked rows
  const [mobileMenuTabId, setMobileMenuTabId] = useState<string | null>(null);
  const menuAnchorRef = useRef<HTMLButtonElement>(null);

  const setTopBarConfig = useTopBarStore((s) => s.setTopBarConfig);
  const resetTopBarConfig = useTopBarStore((s) => s.resetTopBarConfig);

  useEffect(() => {
    setTopBarConfig({
      title: 'کتابخانه',
      showLogo: false,
      hideDefaultActions: true,
      actions: (
        <>
          <IconButton
            variant="ghost"
            size="md"
            icon={<ListPlus className="size-5" />}
            label="لیست پخش جدید"
            data-testid="library-mobile-new-playlist-button"
            onClick={() => setIsNewPlaylistModalOpen(true)}
          />
          <IconButton
            variant="primary"
            size="md"
            icon={<Upload className="size-5" />}
            label="وارد کردن فایل"
            data-testid="library-mobile-import-button"
            onClick={() => setIsUploadModalOpen(true)}
          />
        </>
      )
    });
    return () => {
      resetTopBarConfig();
    };
  }, [setTopBarConfig, resetTopBarConfig]);

  useEffect(() => {
    loadLibraryData();
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    // Pull moderation state of own tabs; the Library reloads when something changed
    window.addEventListener(LIBRARY_CHANGED_EVENT, loadLibraryData);
    void refreshOwnTabStatuses();
    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener(LIBRARY_CHANGED_EVENT, loadLibraryData);
    };
  }, []);

  const loadLibraryData = async () => {
    try {
      const localTabs = await getLocalTabs();
      const favs = await getLocalFavorites();
      const pls = await getLocalPlaylists();

      setTabs(localTabs);
      setFavoriteTabIds(new Set(favs.map((f) => f.tabId)));
      setPlaylists(pls);
    } catch (err) {
      console.error('Failed to load library data from Dexie:', err);
    }
  };

  const favoriteBusy = useRef(new Set<string>());

  const handleToggleFavorite = async (tabId: string) => {
    // Ignore clicks while this tab's own toggle is still writing to Dexie (double-click race)
    if (favoriteBusy.current.has(tabId)) return;
    favoriteBusy.current.add(tabId);
    try {
      const isFav = await toggleFavoriteTab(tabId);
      setFavoriteTabIds((prev) => {
        const next = new Set(prev);
        if (isFav) next.add(tabId);
        else next.delete(tabId);
        return next;
      });

      // On/off state is pushed now, or on reconnect / next sync if offline or the request fails
      requestFavoriteSync();
    } catch (err) {
      console.error('Error toggling favorite:', err);
    } finally {
      favoriteBusy.current.delete(tabId);
    }
  };

  const handleDeleteTab = (tabId: string) => {
    const deletedTab = tabs.find((t) => t.id === tabId);
    if (!deletedTab) return;

    // Optimistically unmount tab from list
    setTabs((prev) => prev.filter((t) => t.id !== tabId));

    // Schedule permanent deletion after 4 seconds
    const { cancel } = stageDeletion(tabId, deletedTab, async () => {
      await deleteLocalTab(tabId);
      // An own private tab is deleted on the server too (FRE-142 D10)
      requestTabUploadSync();
    });

    const { user, isGuest } = useAuthStore.getState();
    const isOwn = !isGuest && !!user && (deletedTab.uploaderId === user.id || deletedTab.ownerId === user.id);

    let undoMessage = 'تبلچر از حافظه حذف شد.';
    if (isOwn && deletedTab.status === 'PENDING') {
      undoMessage = 'ارسال برای بررسی لغو و تبلچر حذف می‌شود.';
    } else if (isOwn && deletedTab.status === 'APPROVED') {
      undoMessage = 'تبلچر از این دستگاه حذف شد؛ در بازارچه باقی می‌ماند.';
    }

    // 4-second Undo Toast
    toast.undo(undoMessage, async () => {
      cancel();
      await saveLocalTab(deletedTab);
      setTabs((prev) => {
        if (prev.some((t) => t.id === tabId)) return prev;
        return [deletedTab, ...prev];
      });
    });
  };

  const handleRetryBackup = async (tabId: string) => {
    await retryTabUpload(tabId);
    await loadLibraryData();
    requestTabUploadSync();
    toast.info('دوباره برای پشتیبان‌گیری تلاش می‌شود.');
  };

  const handleCreatePlaylist = async () => {
    if (!newPlaylistTitle.trim()) return;

    const newPl = await saveLocalPlaylist({
      id: newId(),
      title: newPlaylistTitle.trim(),
      createdAt: new Date().toISOString(),
      tabIds: []
    });
    requestPlaylistSync();
    setPlaylists((prev) => [...prev, newPl]);
    setNewPlaylistTitle('');
    setIsNewPlaylistModalOpen(false);
    setActiveSegment('playlists');
    toast.success(`لیست پخش "${newPl.title}" ایجاد شد.`);
  };

  const openRenamePlaylist = (pl: LocalPlaylist) => {
    setRenamePlaylistId(pl.id);
    setRenamePlaylistTitle(pl.title);
  };

  const closeRenamePlaylist = () => {
    setRenamePlaylistId(null);
    setRenamePlaylistTitle('');
  };

  const handleRenamePlaylist = async () => {
    const title = renamePlaylistTitle.trim();
    if (!renamePlaylistId || !title) return;

    const current = playlists.find((p) => p.id === renamePlaylistId);
    if (current && current.title !== title) {
      const renamed = await renameLocalPlaylist(renamePlaylistId, title);
      if (renamed) {
        requestPlaylistSync();
        setPlaylists((prev) => prev.map((p) => (p.id === renamed.id ? renamed : p)));
        toast.success(`نام لیست پخش به "${title}" تغییر کرد.`);
      }
    }
    closeRenamePlaylist();
  };

  const handleDeletePlaylist = (id: string) => {
    const deletedPlaylist = playlists.find((p) => p.id === id);
    if (!deletedPlaylist) return;

    setPlaylists((prev) => prev.filter((p) => p.id !== id));
    if (selectedPlaylistId === id) {
      setSelectedPlaylistId(null);
    }

    const { cancel } = stageDeletion(id, deletedPlaylist, async () => {
      await deleteLocalPlaylist(id);
      requestPlaylistSync();
    });

    toast.undo('لیست پخش حذف شد.', async () => {
      cancel();
      await saveLocalPlaylist(deletedPlaylist);
      requestPlaylistSync();
      setPlaylists((prev) => {
        if (prev.some((p) => p.id === id)) return prev;
        return [...prev, deletedPlaylist];
      });
    });
  };

  const handleAddTabToPlaylist = async (playlistId: string, tabId: string) => {
    await addTabToPlaylist(playlistId, tabId);
    requestPlaylistSync();
    const updated = await getLocalPlaylists();
    setPlaylists(updated);
    setPlaylistTargetTabId(null);
    toast.success('تبلچر به لیست پخش افزوده شد.');
  };

  useEffect(() => {
    try {
      localStorage.setItem(SORT_STORAGE_KEY, JSON.stringify({ key: sortKey, dir: sortDir }));
    } catch {
      // storage unavailable: the choice just won't persist
    }
  }, [sortKey, sortDir]);

  // Filter tabs according to search, tuning, and active segment, then sort
  const getFilteredTabs = () => {
    const filtered = tabs.filter((t) => {
      const matchesSearch =
        searchQuery === '' ||
        fold(t.title).includes(fold(searchQuery)) ||
        fold(t.artist).includes(fold(searchQuery));

      if (!matchesSearch) return false;
      if (!matchesTuning(t.tuning, selectedTuning)) return false;

      if (activeSegment === 'favorites') {
        return favoriteTabIds.has(t.id);
      }

      if (activeSegment === 'playlists' && selectedPlaylistId) {
        const pl = playlists.find((p) => p.id === selectedPlaylistId);
        return pl ? pl.tabIds.includes(t.id) : false;
      }

      return true;
    });

    const sign = sortDir === 'asc' ? 1 : -1;
    const sorted = filtered.sort((a, b) => sign * compareTabs(a, b, sortKey));

    // FND-05: no title+artist dedupe; every tab stays reachable and deletable.
    return sorted;
  };

  // Tuning options come from the tunings present in the library; the static list is a fallback.
  const libraryTunings = Array.from(new Set(tabs.map((t) => formatTuningBadge(t.tuning))));
  const tuningOptions =
    libraryTunings.length > 0
      ? [
          TUNING_OPTIONS[0]!,
          ...libraryTunings.map((value) => ({ value, label: value }))
        ]
      : TUNING_OPTIONS;

  const filteredTabs = getFilteredTabs();
  const selectedPlaylist = playlists.find((p) => p.id === selectedPlaylistId);

  // Segmented options with accessible data-testids
  const segmentedOptions = [
    {
      value: 'all' as const,
      label: 'همه',
      count: toPersianDigits(tabs.length),
      icon: <span data-testid="library-segment-all" className="sr-only" />
    },
    {
      value: 'favorites' as const,
      label: 'علاقه‌مندی‌ها',
      shortLabel: 'علاقه‌مندی',
      count: toPersianDigits(favoriteTabIds.size),
      icon: <span data-testid="library-segment-favorites" className="sr-only" />
    },
    {
      value: 'playlists' as const,
      label: 'لیست‌های پخش',
      shortLabel: 'لیست‌ها',
      count: toPersianDigits(playlists.length),
      icon: <span data-testid="library-segment-playlists" className="sr-only" />
    }
  ];

  return (
    <div className="flex flex-col gap-5 max-w-6xl mx-auto">
      {/* 1. Page Header */}
      <div data-testid="library-page-header" className="hidden lg:block">
        <PageHeader
          title="کتابخانه"
          subtitle={
            tabs.every((t) => t.localBlob)
              ? `${toPersianDigits(tabs.length)} تبلچر روی این دستگاه، همه بدون اینترنت قابل پخش.`
              : `${toPersianDigits(tabs.length)} تبلچر در کتابخانه؛ ${toPersianDigits(tabs.filter((t) => t.localBlob).length)} تبلچر روی این دستگاه و بدون اینترنت قابل پخش است.`
          }
          actions={
            <>
              <Button
                variant="secondary"
                size="md"
                leadingIcon={<Plus className="size-4" />}
                onClick={() => setIsNewPlaylistModalOpen(true)}
                data-testid="library-new-playlist-button"
              >
                <span>لیست پخش</span>
                <span className="sr-only"> جدید</span>
              </Button>
              <Button
                variant="primary"
                size="md"
                leadingIcon={<Upload className="size-4" />}
                onClick={() => setIsUploadModalOpen(true)}
                data-testid="library-import-button"
              >
                وارد کردن فایل
              </Button>
            </>
          }
        />
      </div>

      {/* 2. Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Live Search Field (Mobile: Row 1 order-1; Desktop: Row 1 flex-1 order-2) */}
        <div className="w-full lg:w-auto lg:flex-1 min-w-[220px] lg:min-w-[150px] order-1 lg:order-2">
          <SearchInput
            data-testid="library-search-input"
            placeholder="جستجوی آهنگ یا هنرمند"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onClear={() => setSearchQuery('')}
            className="w-full [&>input]:bg-base-200 [&>input]:border-base-300 [&>input]:h-11"
          />
        </div>

        {/* Segmented Tabs (Mobile: Row 2 order-2; Desktop: Row 1 order-1) */}
        <div className="w-full lg:w-auto order-2 lg:order-1" data-testid="library-segmented-tabs">
          <Segmented
            options={segmentedOptions}
            value={activeSegment}
            onChange={(val) => {
              setActiveSegment(val);
              if (val !== 'playlists') setSelectedPlaylistId(null);
            }}
            fullWidth={isMobile}
            className="w-full lg:w-auto bg-base-200 border-base-300"
          />
        </div>

        {/* Desktop Tuning Filter Select */}
        <div className="hidden lg:flex items-center order-3">
          <label htmlFor="lib-tuning" className="sr-only">
            کوک
          </label>
          <select
            id="lib-tuning"
            data-testid="library-tuning-filter"
            value={selectedTuning}
            onChange={(e) => setSelectedTuning(e.target.value)}
            className="h-11 md:h-10 w-[113px] min-w-0 truncate border border-base-300 rounded-xl bg-base-200 text-base-content px-3 text-sm focus-visible:outline-none focus-visible:border-primary select-none cursor-pointer"
          >
            {tuningOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Sort: field select + ascending/descending toggle */}
        <div className="flex items-center gap-1.5 order-5 lg:order-4 ms-auto lg:ms-0" data-testid="library-sort">
          <label htmlFor="lib-sort" className="sr-only">
            مرتب‌سازی
          </label>
          <select
            id="lib-sort"
            data-testid="library-sort-select"
            value={sortKey}
            onChange={(e) => setSortKey(e.target.value as SortKey)}
            className="h-11 md:h-10 min-w-[130px] border border-base-300 rounded-xl bg-base-200 text-base-content px-3 text-xs sm:text-sm focus-visible:outline-none focus-visible:border-primary select-none cursor-pointer"
          >
            {SORT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <IconButton
            data-testid="library-sort-direction"
            label={sortDir === 'asc' ? 'ترتیب صعودی' : 'ترتیب نزولی'}
            title={sortDir === 'asc' ? 'ترتیب صعودی' : 'ترتیب نزولی'}
            aria-pressed={sortDir === 'desc'}
            variant="ghost"
            size="md"
            icon={sortDir === 'asc' ? <ArrowUp className="size-4" /> : <ArrowDown className="size-4" />}
            onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
            className="size-10 rounded-xl border border-base-300 bg-base-200"
          />
        </div>

        {/* Desktop View Mode Switcher (Grid vs List) */}
        <div className="hidden lg:inline-flex items-center gap-1 p-1 bg-base-200 border border-base-300 rounded-xl select-none order-5">
          <IconButton
            label="نمای شبکه‌ای"
            title="نمای شبکه‌ای"
            aria-pressed={viewMode === 'grid'}
            variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
            size="sm"
            icon={<LayoutGrid className="size-4" />}
            onClick={() => setViewMode('grid')}
            className={`size-8 rounded-[9px] ${
              viewMode === 'grid' ? 'bg-base-300 text-base-content' : 'text-base-content/60'
            }`}
          />
          <IconButton
            label="نمای فهرستی"
            title="نمای فهرستی"
            aria-pressed={viewMode === 'list'}
            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
            size="sm"
            icon={<List className="size-4" />}
            onClick={() => setViewMode('list')}
            className={`size-8 rounded-[9px] ${
              viewMode === 'list' ? 'bg-base-300 text-base-content' : 'text-base-content/60'
            }`}
          />
        </div>
      </div>

      {/* Mobile Tuning Filter Chips (Mobile: Row 3) */}
      <div dir="rtl" className="flex lg:hidden items-center justify-start gap-2 overflow-x-auto -mt-2 no-scrollbar">
        {tuningOptions.map((opt) => (
          <Chip
            key={opt.value}
            mono={opt.value !== 'all'}
            size="md"
            selected={selectedTuning === opt.value}
            onClick={() => setSelectedTuning(opt.value)}
            className="shrink-0"
          >
            {opt.label}
          </Chip>
        ))}
      </div>

      {/* 3. Main Content Display */}
      {/* 3.A: Playlists View */}
      {activeSegment === 'playlists' && !selectedPlaylistId && (
        <div className="flex flex-col gap-4">
          {playlists.length === 0 ? (
            <EmptyState
              data-testid="library-empty-state"
              icon={<FolderPlus />}
              title="لیست پخشی ساخته نشده است."
              description="برای دسته‌بندی قطعات تمرینی، اولین لیست پخش خود را بسازید."
              action={
                <Button
                  variant="primary"
                  size="md"
                  leadingIcon={<Plus className="size-4" />}
                  onClick={() => setIsNewPlaylistModalOpen(true)}
                >
                  ساخت اولین لیست پخش
                </Button>
              }
            />
          ) : (
            <div className="bg-base-200 border border-base-300 rounded-2xl overflow-hidden divide-y divide-base-300">
              {playlists.map((pl) => (
                <div
                  key={pl.id}
                  className="flex items-center justify-between gap-3 p-4 hover:bg-base-300/40 transition group"
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <span className="size-11 shrink-0 rounded-xl bg-neutral text-primary-soft flex items-center justify-center">
                      <FolderPlus className="size-5" />
                    </span>
                    <div className="flex flex-col min-w-0 flex-1 text-start">
                      <button
                        type="button"
                        onClick={() => setSelectedPlaylistId(pl.id)}
                        className="text-sm font-semibold text-base-content hover:text-primary transition truncate text-start max-md:py-3 max-md:-my-2"
                      >
                        {pl.title}
                      </button>
                      <span className="text-xs text-base-content/60 mt-0.5">
                        {toPersianDigits(pl.tabIds.length)} تبلچر
                      </span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 shrink-0">
                    <IconButton
                      data-testid={`playlist-rename-${pl.id}`}
                      label="تغییر نام لیست پخش"
                      title="تغییر نام لیست پخش"
                      icon={<Pencil className="size-4" />}
                      variant="ghost"
                      size="sm"
                      onClick={() => openRenamePlaylist(pl)}
                    />
                    <IconButton
                      label="حذف لیست پخش"
                      title="حذف لیست پخش"
                      icon={<Trash2 className="size-4" />}
                      variant="ghost"
                      size="sm"
                      className="text-error-soft hover:bg-error/10 hover:text-error"
                      onClick={() => handleDeletePlaylist(pl.id)}
                    />
                    <IconButton
                      label="مشاهده لیست پخش"
                      title="مشاهده لیست پخش"
                      icon={<ChevronLeft className="size-4" />}
                      variant="ghost"
                      size="sm"
                      onClick={() => setSelectedPlaylistId(pl.id)}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 3.B: Playlist Detail View */}
      {activeSegment === 'playlists' && selectedPlaylistId && selectedPlaylist && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-3 p-3 bg-base-200 border border-base-300 rounded-xl">
            <div className="flex items-center gap-3 min-w-0">
              <IconButton
                label="بازگشت به همه لیست‌ها"
                title="بازگشت به همه لیست‌ها"
                icon={<ChevronRight className="size-5" />}
                variant="ghost"
                size="sm"
                onClick={() => setSelectedPlaylistId(null)}
              />
              <div className="flex flex-col text-start min-w-0">
                <span className="font-bold text-base text-base-content line-clamp-2 break-words">
                  {selectedPlaylist.title}
                </span>
                <span className="text-xs text-base-content/60">
                  {toPersianDigits(filteredTabs.length)} تبلچر
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <IconButton
                data-testid="playlist-detail-rename"
                label="تغییر نام لیست پخش"
                title="تغییر نام لیست پخش"
                icon={<Pencil className="size-4" />}
                variant="ghost"
                size="sm"
                onClick={() => openRenamePlaylist(selectedPlaylist)}
              />
              <IconButton
                label="حذف لیست پخش"
                title="حذف لیست پخش"
                icon={<Trash2 className="size-4" />}
                variant="ghost"
                size="sm"
                className="text-error-soft hover:bg-error/10 hover:text-error"
                onClick={() => handleDeletePlaylist(selectedPlaylist.id)}
              />
            </div>
          </div>

          {filteredTabs.length === 0 ? (
            <EmptyState
              data-testid="library-empty-state"
              icon={<Music />}
              title="این لیست پخش خالی است."
              description="تبلچرهایی را از بخش «همه» با گزینهٔ «افزودن به لیست پخش» به اینجا اضافه کنید."
              action={
                <Button variant="secondary" size="sm" onClick={() => setActiveSegment('all')}>
                  مرور کتابخانه
                </Button>
              }
            />
          ) : (
            renderTabsList(filteredTabs)
          )}
        </div>
      )}

      {/* 3.C: All Tabs & Favorites View */}
      {activeSegment !== 'playlists' && (
        <div>
          {tabs.length === 0 ? (
            /* Empty Library */
            <EmptyState
              data-testid="library-empty-state"
              icon={<Music />}
              title="هنوز تبلچری ندارید"
              description="یک فایل Guitar Pro وارد کنید تا بدون نیاز به اینترنت تمرین کنید."
              action={
                <Button
                  variant="primary"
                  size="md"
                  leadingIcon={<Upload className="size-4" />}
                  onClick={() => setIsUploadModalOpen(true)}
                >
                  وارد کردن فایل
                </Button>
              }
              secondaryAction={
                <Button
                  variant="secondary"
                  size="md"
                  onClick={() => navigate('/marketplace')}
                >
                  مرور بازارچه
                </Button>
              }
            />
          ) : activeSegment === 'favorites' && favoriteTabIds.size === 0 ? (
            /* Empty Favorites */
            <EmptyState
              data-testid="library-empty-state"
              icon={<Heart />}
              title="تبلچری به علاقه‌مندی‌ها اضافه نشده است."
              description="با کلیک روی آیکون قلب روی هر تبلچر، آن را به لیست علاقه‌مندی‌ها اضافه کنید."
            />
          ) : filteredTabs.length === 0 ? (
            /* Empty Filter / Search Results */
            <EmptyState
              data-testid="library-empty-state"
              icon={<Search />}
              title="تبلچری با این مشخصات یافت نشد"
              description="عبارت جستجو یا فیلترهای کوک را تغییر دهید."
              action={
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedTuning('all');
                  }}
                >
                  پاک کردن فیلترها
                </Button>
              }
            />
          ) : (
            renderTabsList(filteredTabs)
          )}
        </div>
      )}

      {/* Footer hint */}
      <p className="text-xs text-base-content/60 text-center lg:text-start pt-2">
        <span className="lg:hidden">
          نگه‌داشتن یا کشیدن ردیف به چپ: افزودن به لیست، حذف
        </span>
        <span className="hidden lg:inline">
          منوی «بیشتر» روی هر کارت: افزودن به لیست پخش، حذف از دستگاه (با «بازگرداندن» در Toast).
        </span>
      </p>

      {/* 4. Modals and Overlays */}
      {/* 4.A: New Playlist Modal */}
      <Dialog
        open={isNewPlaylistModalOpen}
        onClose={() => setIsNewPlaylistModalOpen(false)}
        title="ایجاد لیست پخش جدید"
        titleHeadingLevel="h3"
        description="یک نام برای دسته‌بندی تبلچرهای خود وارد کنید."
        size="sm"
        data-testid="new-playlist-dialog"
        footer={
          <>
            <Button
              variant="primary"
              onClick={handleCreatePlaylist}
              data-testid="dialog-primary-action"
            >
              ایجاد
            </Button>
            <Button
              variant="ghost"
              onClick={() => setIsNewPlaylistModalOpen(false)}
              data-testid="dialog-secondary-action"
            >
              انصراف
            </Button>
          </>
        }
      >
        <Field label="نام لیست پخش" required>
          <Input
            type="text"
            placeholder="نام لیست پخش (مثال: سولوهای سرعتی متال)"
            value={newPlaylistTitle}
            onChange={(e) => setNewPlaylistTitle(e.target.value)}
            maxLength={200}
            autoFocus
          />
        </Field>
      </Dialog>

      {/* 4.A2: Rename Playlist Modal */}
      <Dialog
        open={Boolean(renamePlaylistId)}
        onClose={closeRenamePlaylist}
        title="تغییر نام لیست پخش"
        titleHeadingLevel="h3"
        description="نام جدید لیست پخش را وارد کنید."
        size="sm"
        data-testid="rename-playlist-dialog"
        footer={
          <>
            <Button
              variant="primary"
              onClick={handleRenamePlaylist}
              disabled={!renamePlaylistTitle.trim()}
              data-testid="dialog-primary-action"
            >
              ذخیره
            </Button>
            <Button variant="ghost" onClick={closeRenamePlaylist} data-testid="dialog-secondary-action">
              انصراف
            </Button>
          </>
        }
      >
        <Field label="نام لیست پخش" required>
          <Input
            type="text"
            data-testid="rename-playlist-input"
            value={renamePlaylistTitle}
            onChange={(e) => setRenamePlaylistTitle(e.target.value)}
            maxLength={200}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                void handleRenamePlaylist();
              }
            }}
            autoFocus
          />
        </Field>
      </Dialog>

      {/* 4.B: Add Tab to Playlist Modal */}
      <Dialog
        open={Boolean(playlistTargetTabId)}
        onClose={() => setPlaylistTargetTabId(null)}
        title="افزودن به لیست پخش"
        description="لیست پخش مورد نظر را انتخاب کنید."
        size="sm"
        data-testid="add-to-playlist-dialog"
        footer={
          <Button
            variant="ghost"
            onClick={() => setPlaylistTargetTabId(null)}
            data-testid="dialog-secondary-action"
          >
            انصراف
          </Button>
        }
      >
        <div className="flex flex-col gap-2 max-h-60 overflow-y-auto">
          {playlists.length === 0 ? (
            <div className="text-center py-6 flex flex-col items-center gap-3">
              <p className="text-xs text-base-content/60">هنوز لیست پخشی ایجاد نشده است.</p>
              <Button
                variant="secondary"
                size="sm"
                leadingIcon={<Plus className="size-4" />}
                onClick={() => {
                  setPlaylistTargetTabId(null);
                  setIsNewPlaylistModalOpen(true);
                }}
              >
                ساخت لیست پخش
              </Button>
            </div>
          ) : (
            playlists.map((pl) => (
              <button
                key={pl.id}
                type="button"
                onClick={() => handleAddTabToPlaylist(pl.id, playlistTargetTabId!)}
                className="flex items-center justify-between gap-3 p-3 rounded-xl border border-base-300 hover:border-primary/50 hover:bg-base-100 transition text-start"
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <FolderPlus className="size-4 text-primary-soft shrink-0" />
                  <span className="text-sm font-semibold text-base-content truncate">
                    {pl.title}
                  </span>
                </div>
                <span className="text-xs text-base-content/60 font-mono">
                  {toPersianDigits(pl.tabIds.length)}
                </span>
              </button>
            ))
          )}
        </div>
      </Dialog>

      {/* 4.C: Tab Import Dialog */}
      <TabImportDialog
        open={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUploaded={(newTab) => {
          setTabs((prev) => [newTab, ...prev]);
          toast.success(
            `تبلچر "${newTab.title}" در کتابخانه ذخیره شد.` +
              (isGuest && newTab.pendingUpload === 1 ? ' برای پشتیبان‌گیری در حساب کاربری، وارد شوید.' : '')
          );
        }}
      />
      {/* 4.D: Tab Details Dialog */}
      {editDetailsTab && (
        <TabDetailsDialog
          open={Boolean(editDetailsTab)}
          onClose={() => setEditDetailsTab(null)}
          tab={editDetailsTab}
          onSaved={(savedTab) => {
            setTabs((prev) => prev.map((t) => (t.id === savedTab.id ? { ...t, ...savedTab } : t)));
          }}
        />
      )}
    </div>
  );

  /**
   * Helper function to render tab list in grid or list view
   */
  function renderTabsList(tabList: TabSong[]) {
    if (!isMobile && viewMode === 'grid') {
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {tabList.map((tab) => (
            <TabCard
              key={tab.id}
              tab={tab}
              isFavorite={favoriteTabIds.has(tab.id)}
              onToggleFavorite={handleToggleFavorite}
              onOpen={(id) => navigate(`/tabs/${id}`)}
              onAddToPlaylist={(id) => setPlaylistTargetTabId(id)}
              onDelete={handleDeleteTab}
              isGuest={isGuest}
              onRetryBackup={handleRetryBackup}
              onEditDetails={handleOpenEditDetails}
            />
          ))}
        </div>
      );
    }

    return (
      <div className="bg-base-200 border border-base-300 rounded-2xl overflow-hidden divide-y divide-base-300">
        {tabList.map((tab) => {
          const isFav = favoriteTabIds.has(tab.id);
          const isMenuOpen = mobileMenuTabId === tab.id;
          return (
            <div
              key={tab.id}
              data-testid={`tab-card-${tab.id}`}
              className="flex items-center gap-3 p-3 hover:bg-base-300/30 transition relative"
            >
              {/* 44×44 Icon Tile */}
              <span className="size-11 shrink-0 rounded-xl bg-neutral text-primary-soft flex items-center justify-center">
                <Music className="size-5" />
              </span>

              {/* Clickable title and artist info */}
              <div
                role="button"
                tabIndex={0}
                onClick={() => navigate(`/tabs/${tab.id}`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    navigate(`/tabs/${tab.id}`);
                  }
                }}
                data-testid={`tab-card-open-${tab.id}`}
                aria-label={`باز کردن تبلچر ${tab.title} اثر ${tab.artist}`}
                className="flex-1 min-w-0 cursor-pointer text-start flex flex-col gap-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-lg"
              >
                <span
                  dir="auto"
                  data-testid={`tab-card-title-${tab.id}`}
                  className="text-[15px] font-semibold text-base-content truncate block text-start"
                >
                  <bdi dir="auto">{tab.title}</bdi>
                </span>
                <span className="text-xs text-base-content/60 flex items-center gap-1.5 text-start min-w-0 overflow-hidden">
                  <span dir="auto" className="truncate shrink min-w-0"><bdi dir="auto">{tab.artist}</bdi></span> ·{' '}
                  <span dir="ltr" className="ltr-nums font-mono shrink-0 whitespace-nowrap inline-block">
                    {formatTuningBadge(tab.tuning)}
                  </span>
                  <ModerationBadge status={tab.status} tabId={tab.id} />
                  <TabBackupBadge tab={tab} isGuest={isGuest} />
                </span>
                <DifficultySummary
                  rhythm={tab.rhythmDifficulty}
                  lead={tab.leadDifficulty}
                  size="sm"
                  className="mt-0.5"
                />
                <RejectReasonNote tab={tab} />
              </div>

              {/* Actions: Favorite Heart & More Menu Separation (44x44pt target & gap-3) */}
              <div className="flex items-center gap-3 shrink-0">
                <IconButton
                  data-testid={`tab-card-favorite-${tab.id}`}
                  label={isFav ? 'حذف از علاقه‌مندی‌ها' : 'افزودن به علاقه‌مندی‌ها'}
                  title={isFav ? 'حذف از علاقه‌مندی‌ها' : 'افزودن به علاقه‌مندی‌ها'}
                  icon={<Heart className={`size-4.5 ${isFav ? 'fill-current' : ''}`} />}
                  variant="ghost"
                  size="sm"
                  className={`size-11 min-w-[44px] min-h-[44px] rounded-xl ${
                    isFav
                      ? 'text-error-soft hover:text-error'
                      : 'text-base-content/40 hover:text-error-soft'
                  }`}
                  onClick={() => handleToggleFavorite(tab.id)}
                />

                {/* More Menu Dropdown */}
                <div className="relative">
                  <IconButton
                    ref={isMenuOpen ? menuAnchorRef : undefined}
                    label="گزینه‌های بیشتر"
                    title="گزینه‌های بیشتر"
                    icon={<MoreHorizontal className="size-4" />}
                    variant="ghost"
                    size="sm"
                    className="size-11 min-w-[44px] min-h-[44px] rounded-xl text-base-content/60 hover:text-base-content"
                    data-testid={`tab-card-more-${tab.id}`}
                    onClick={() =>
                      setMobileMenuTabId((prev) => (prev === tab.id ? null : tab.id))
                    }
                    aria-expanded={isMenuOpen}
                  />

                {isMenuOpen && (
                  <AnchoredMenu
                    open
                    anchorRef={menuAnchorRef}
                    onClose={() => setMobileMenuTabId(null)}
                    className="min-w-[170px] p-1.5 rounded-xl bg-base-200 border border-base-300 shadow-xl flex flex-col gap-0.5 select-none"
                  >
                    <button
                      type="button"
                      role="menuitem"
                      data-testid="tab-menu-details"
                      onClick={() => {
                        setMobileMenuTabId(null);
                        handleOpenEditDetails(tab);
                      }}
                      className="flex items-center gap-2.5 w-full min-h-11 px-2.5 py-2 text-xs font-medium rounded-lg text-base-content/80 hover:bg-base-300 hover:text-base-content transition text-start"
                    >
                      <Pencil className="size-4 shrink-0 text-primary-soft" />
                      <span data-testid={`tab-menu-details-${tab.id}`}>ویرایش مشخصات</span>
                    </button>
                    <button
                      type="button"
                      role="menuitem"
                      onClick={() => {
                        setMobileMenuTabId(null);
                        setPlaylistTargetTabId(tab.id);
                      }}
                      className="flex items-center gap-2.5 w-full min-h-11 px-2.5 py-2 text-xs font-medium rounded-lg text-base-content/80 hover:bg-base-300 hover:text-base-content transition text-start"
                    >
                      <FolderPlus className="size-4 shrink-0 text-primary-soft" />
                      <span>افزودن به لیست پخش</span>
                    </button>
                    {!isGuest && tab.pendingUpload === 1 && tab.uploadBlocked === 1 && (
                      <button
                        type="button"
                        role="menuitem"
                        data-testid={`tab-card-retry-backup-${tab.id}`}
                        onClick={() => {
                          setMobileMenuTabId(null);
                          void handleRetryBackup(tab.id);
                        }}
                        className="flex items-center gap-2.5 w-full min-h-11 px-2.5 py-2 text-xs font-medium rounded-lg text-base-content/80 hover:bg-base-300 hover:text-base-content transition text-start"
                      >
                        <RefreshCw className="size-4 shrink-0 text-primary-soft" />
                        <span>تلاش دوباره برای پشتیبان‌گیری</span>
                      </button>
                    )}
                    <button
                      type="button"
                      role="menuitem"
                      data-testid={`tab-menu-delete-${tab.id}`}
                      onClick={() => {
                        setMobileMenuTabId(null);
                        handleDeleteTab(tab.id);
                      }}
                      className="flex items-center gap-2.5 w-full min-h-11 px-2.5 py-2 text-xs font-medium rounded-lg text-error-soft hover:bg-error/10 hover:text-error transition text-start"
                    >
                      <Trash2 className="size-4 shrink-0" />
                      <span>حذف از حافظه</span>
                    </button>
                  </AnchoredMenu>
                )}
              </div>
            </div>
          </div>
        );
        })}
      </div>
    );
  }
};
